import { AgGridReact } from "ag-grid-react";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import "../../assets/css/AGGrid-custom.css";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { useDealProvider } from "../../context/DealContext";
import { renderSubTitle } from "../../components/renderComponents";
import { memo, useEffect, useRef, useState } from "react";
import { apiGet, apiGetById } from "../../services/genesisApi/httpsMethods";
import { useParams } from "react-router-dom";
import { numberFormatter, dateFormatter } from "../../utils/numberFormatter";
import { Box, IconButton } from "@mui/material";
import { CustomButton } from "../../components/common/customButton";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  hasAnyDealTenantAssetNameError,
  validateDealTenantAssetNameValue,
} from "../deal/dealHelperFunctions/dealHelperFunctions";
import {
  createAreaTypeSelectColumn,
  createStringSelectColumn,
} from "../../utils/columnHelpers";
import { useReferenceLists } from "../../context/GlobalGetLists";

ModuleRegistry.registerModules([AllCommunityModule]);

const ExtractedTenantsTable = ({
  assetId,
  editMode,
  syncTenantAssetNameErrorToContext = false,
}: {
  assetId: number | null;
  editMode: boolean;
  /** When true, drives DealContext.tenantAssetNameFieldHasError from tenant rows + asset_name validation. */
  syncTenantAssetNameErrorToContext?: boolean;
}) => {
  const {
    tenants,
    setTenants,
    updateTenant,
    addTenant,
    removeTenant,
    assets,
    setTenantAssetNameFieldHasError,
  } = useDealProvider();

  const [assetObject, setAssetObject] = useState<{
    id: number;
    asset_name: string;
  } | null>(null);
  const { dealId } = useParams();
  const gridRef = useRef<any>(null);
  const { currencies } = useReferenceLists();

  useEffect(() => {
    if (assetId) {
      apiGetById("asset", assetId)
        .then((response) => {
          if (response && response.success && response.data) {
            setAssetObject({
              id: response.data[0].id,
              asset_name: response.data[0].asset_name,
            });
          }
        })
        .catch((err) => console.error("Failed to fetch asset", err));
    } else {
      setAssetObject(null);
    }

    if (dealId) {
      apiGet(`tenants?dealId=${dealId}`)
        .then((response) => {
          if (response && response.success && response.data) {
            // Merge with existing tenants instead of replacing
            // This preserves extraction tenants that haven't been saved to DB yet

            setTenants((prevTenants) => {
              // If no existing tenants, just set the fetched ones
              if (!prevTenants || prevTenants.length === 0) {
                return response.data;
              }

              // Merge: keep existing tenants (extraction data) and add fetched ones that aren't already there
              const existingIds = new Set(
                prevTenants.map((t: any) => t.id).filter(Boolean),
              );
              const newTenants = response.data.filter(
                (t: any) => !existingIds.has(t.id),
              );
              return [...prevTenants, ...newTenants];
            });
          }
        })
        .catch((err) => console.error("Failed to fetch tenants", err));
    } else if (assetId) {
      apiGet(`tenants?assetId=${assetId}`)
        .then((response) => {
          if (response && response.success && response.data) {
            // Merge with existing tenants instead of replacing

            setTenants((prevTenants) => {
              if (!prevTenants || prevTenants.length === 0) {
                return response.data;
              }
              const existingIds = new Set(
                prevTenants.map((t: any) => t.id).filter(Boolean),
              );
              const newTenants = response.data.filter(
                (t: any) => !existingIds.has(t.id),
              );
              return [...prevTenants, ...newTenants];
            });
          }
        })
        .catch((err) => console.error("Failed to fetch tenants", err));
    }
  }, [dealId, assetId, setTenants]);

  useEffect(() => {
    if (!syncTenantAssetNameErrorToContext) return;
    if (assetId || !editMode) {
      setTenantAssetNameFieldHasError(false);
      return;
    }
    setTenantAssetNameFieldHasError(
      hasAnyDealTenantAssetNameError(tenants, assets),
    );
  }, [
    syncTenantAssetNameErrorToContext,
    assetId,
    editMode,
    tenants,
    assets,
    setTenantAssetNameFieldHasError,
  ]);

  useEffect(() => {
    if (!syncTenantAssetNameErrorToContext) return;
    return () => setTenantAssetNameFieldHasError(false);
  }, [syncTenantAssetNameErrorToContext, setTenantAssetNameFieldHasError]);

  const handleCheckboxChange = (
    checked: boolean,
    tenantId: number | string,
    rowData?: any,
  ) => {
    // Checkbox controls whether tenant should be included/uploaded
    // When unchecking, stash the previous asset so re-check can restore it (especially in deal context).
    const prevAsset = rowData?.asset;
    const prevAssetId = rowData?.asset_id ?? rowData?.asset?.id;

    // Build update object in a single call to avoid multiple updates
    const update: any = {
      shouldCreate: checked,
      included: checked,
    };

    if (!checked) {
      // Mark as not included, but preserve asset association
      update.prev_asset = prevAsset;
      update.prev_asset_id = prevAssetId;
    } else {
      // If we're on an asset page, also associate to the current asset.
      if (assetObject?.id) {
        update.asset = assetObject;
        update.asset_id = assetObject.id;
      } else {
        // Otherwise, restore the previous asset association (if we have one).
        const restoreAsset = rowData?.prev_asset;
        const restoreAssetId = rowData?.prev_asset_id;
        if (restoreAsset || restoreAssetId) {
          update.asset = restoreAsset;
          update.asset_id = restoreAssetId;
        }
      }
    }

    // Single update call
    console.log("update");
    updateTenant(tenantId, update);

    // Defer grid refresh to next frame to avoid blocking the click handler
    requestAnimationFrame(() => {
      if (gridRef.current) {
        gridRef.current.api.refreshCells({ force: true, suppressFlash: true });
      }
    });
  };

  const handleDeleteRowById = (tenantId: number | string) => {
    if (tenantId === null || tenantId === undefined) return;
    removeTenant(tenantId);
  };

  const columnDefs: ColDef[] = [
    // adjust this for tenants relating to assets
    {
      headerName: "",
      field: "included",
      width: 50,
      editable: false,
      cellRenderer: (params: ICellRendererParams) => {
        // Checkbox controls whether tenant should be included/uploaded
        // Default to true if not explicitly set to false
        const isChecked =
          params.data?.shouldCreate !== false &&
          params.data?.included !== false;

        return (
          <input
            key={`checkbox-${params.data?.id}-${isChecked}`}
            type="checkbox"
            checked={isChecked}
            onChange={(e) => {
              e.stopPropagation();
              handleCheckboxChange(
                e.target.checked,
                params.data?.id,
                params.data,
              );
            }}
          />
        );
      },
      onCellClicked: (params: any) => {
        if (params.column.getColId() === "included") {
          const currentChecked =
            params.data?.shouldCreate !== false &&
            params.data?.included !== false;
          handleCheckboxChange(!currentChecked, params.data?.id, params.data);
        }
      },
    },
    {
      headerName: "",
      field: "delete",
      width: 50,
      editable: false,
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              const rowId = params.data?.id as number | string | undefined;
              if (rowId !== null && rowId !== undefined) {
                handleDeleteRowById(rowId);
              }
            }}
            sx={{ padding: "2px" }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        );
      },
    },
    {
      field: "asset_name",
      headerName: "Asset",
      editable: assetId ? false : editMode,
      width: 130,
      valueGetter: (params: any) => {
        // Try to get asset_name from nested asset object first, then fall back to direct asset_name
        return params.data?.asset?.asset_name || params.data?.asset_name || "";
      },
      // https://www.ag-grid.com/react-data-grid/cell-editing-validation/#overriding-validation
      ...(!assetId && editMode
        ? {
            cellEditorParams: {
              getValidationErrors: (params: { value: unknown }) => {
                const msgs = validateDealTenantAssetNameValue(
                  params.value,
                  assets,
                );
                if (syncTenantAssetNameErrorToContext) {
                  const fromRows = hasAnyDealTenantAssetNameError(
                    tenants,
                    assets,
                  );
                  const fromEditor = msgs != null && msgs.length > 0;
                  setTenantAssetNameFieldHasError(fromRows || fromEditor);
                }

                return msgs;
              },
            },
          }
        : {}),
    },
    {
      field: "tenant_name",
      headerName: "Tenant",
      editable: editMode,
      width: 150,
    },
    {
      field: "tenant_sector",
      headerName: "Tenant Sector",
      editable: editMode,
      width: 150,
    },
    {
      field: "lease_start_date",
      headerName: "Lease Start Date",
      editable: editMode,
      width: 100,
      valueFormatter: dateFormatter,
    },
    {
      field: "lease_end_date",
      headerName: "Lease End Date",
      editable: editMode,
      width: 100,
      valueFormatter: dateFormatter,
    },
    {
      field: "break_option_date",
      headerName: "Break Option Date",
      editable: editMode,
      width: 100,
      valueFormatter: dateFormatter,
    },
    {
      field: "next_review_date",
      headerName: "Next Review Date",
      editable: editMode,
      width: 100,
      valueFormatter: dateFormatter,
    },
    {
      field: "contracted_rent",
      headerName: "Contracted Rent",
      editable: editMode,
      width: 100,
      valueFormatter: numberFormatter,
    },
    {
      field: "passing_rent",
      headerName: "Passing Rent",
      editable: editMode,
      width: 100,
      valueFormatter: numberFormatter,
    },
    {
      field: "rent_free_amount",
      headerName: "Rent Free Amount",
      editable: editMode,
      width: 100,
      valueFormatter: numberFormatter,
    },
    {
      field: "rent_free_end_date",
      headerName: "Rent Free End Date",
      editable: editMode,
      width: 100,
      valueFormatter: dateFormatter,
    },
    createStringSelectColumn(
      "rent_currency",
      "Currency",
      currencies || [],
      100,
      editMode,
    ),
    {
      field: "unit_type",
      headerName: "Unit Type",
      editable: editMode,
      width: 100,
    },
    {
      field: "total_area",
      headerName: "Total Area",
      editable: editMode,
      width: 100,
      valueFormatter: numberFormatter,
    },
    createAreaTypeSelectColumn(editMode, 100),
    {
      field: "estimated_rental_value",
      headerName: "ERV",
      editable: editMode,
      width: 100,
      valueFormatter: numberFormatter,
    },
    {
      field: "tf_erv",
      headerName: "TF ERV",
      editable: editMode,
      width: 100,
      valueFormatter: numberFormatter,
    },
    {
      field: "count_parking",
      headerName: "No. of Parking Spaces",
      editable: editMode,
      width: 100,
      valueFormatter: numberFormatter,
    },
    {
      field: "parking_erv",
      headerName: "Parking ERV",
      editable: editMode,
      width: 100,
      valueFormatter: numberFormatter,
    },
    {
      field: "parking_income",
      headerName: "Parking Income",
      editable: editMode,
      width: 100,
      valueFormatter: numberFormatter,
    },
    {
      field: "ios_area",
      headerName: "IOS Area",
      editable: editMode,
      width: 100,
      valueFormatter: numberFormatter,
    },
    {
      field: "ios_erv",
      headerName: "IOS ERV",
      editable: editMode,
      width: 100,
      valueFormatter: numberFormatter,
    },
    {
      field: "ios_income",
      headerName: "IOS Income",
      editable: editMode,
      width: 100,
      valueFormatter: numberFormatter,
    },
  ];

  const onCellValueChanged = (event: any) => {
    const { data } = event;
    const rowId = data?.id as number | string | undefined;
    if (rowId === null || rowId === undefined) return;

    // Snapshot whole row so valueSetter side-effects (e.g. primitive currency)
    // flow into shared tenant state and reach any subsequent PUT.
    updateTenant(rowId, { ...data } as any);

    setTimeout(() => {
      if (gridRef.current) {
        gridRef.current.api.refreshCells({ force: true });
      }
    }, 0);
  };

  const handleAddRow = () => {
    const newRow = {
      id: `new-tenant-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      tenant_name: "",
      tenant_sector: "",
      asset: assetObject,
      asset_id: assetObject?.id,
    };
    addTenant(newRow as any);
  };

  return (
    <>
      {renderSubTitle("Tenants")}

      <div
        className="ag-theme-alpine"
        style={{ height: 400, width: "100%", marginTop: 10 }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={tenants}
          columnDefs={columnDefs}
          onCellValueChanged={onCellValueChanged}
          singleClickEdit={true}
          stopEditingWhenCellsLoseFocus={true}
          invalidEditValueMode="block"
          rowHeight={30}
          getRowId={({ data }) => {
            // All rows should now have unique IDs
            return (
              data?.id?.toString() ||
              `fallback-tenant-${Math.random().toString(36).substr(2, 9)}`
            );
          }}
        />
      </div>
      {editMode && (
        <Box display="flex" gap={2} mb={1} mt={2}>
          <CustomButton
            handleButtonPress={handleAddRow}
            isButtonDisabled={false}
            solid={false}
            text="Add Row"
          ></CustomButton>
        </Box>
      )}
    </>
  );
};

// Memoized so parent forms (e.g. CreateDealUpdate) can re-render on each
// keystroke without re-rendering this component.
export default memo(ExtractedTenantsTable);
